/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, ReactElement, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import moment from 'moment';
import { Button, Popper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
// Resources
import { addTimeToDate } from '../../utils/date';

const INTERVAL_MINUTE = 15;

export enum FilterMode {
    OFF = 'off',
    ON = 'on',
    AFTER_DATE = 'afterDate',
}

interface PopperTimePickerProps {
    onChangeTime: (data?: any, dateString?: string) => any;
    timeValue: any;
    anchorEl: null | HTMLElement;
    empty: boolean;
    closeModal: (event: any) => void;
    validDate: any;
    filterMode?: FilterMode;
    modalRef?: any;
}

const PopperTimePicker: FC<PopperTimePickerProps> = (props): ReactElement => {
    const {
        onChangeTime,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        closeModal,
        timeValue,
        empty,
        anchorEl,
        validDate,
        filterMode = FilterMode.ON,
        modalRef,
    } = props;
    const classes = useStyles();

    const open = Boolean(anchorEl);
    const timeString = timeValue?.isValid() ? timeValue.format('HH:mm') : timeValue?._i || '';

    const [index, setIndex] = React.useState(-1);
    const [proposals, setProposals] = React.useState([]);
    const popperRef = React.useRef(null);

    useEffect(() => {
        calculateProposals();
    }, [timeValue]);

    const calculateProposals = () => {
        let res = [];
        let newIndex = -1;

        for (let i = 0; i < 24; i++) {
            for (let a = 0; a < 60 / INTERVAL_MINUTE; a++) {
                // Looping 4 times for 15-minute intervals within an hour (60 / 15 = 4)
                const hour = `${i < 10 ? '0' : ''}${i}`;
                const minute = `${INTERVAL_MINUTE * a < 10 ? '0' : ''}${INTERVAL_MINUTE * a}`;
                res.push(`${hour}:${minute}`);
            }
        }

        res = res.filter((el) => {
            if (filterMode === FilterMode.OFF || !timeValue || timeString.includes('/')) {
                return el;
            }
            if (filterMode === FilterMode.AFTER_DATE) {
                return +el.replace(':', '') >= +timeString.replace(':', '');
            }
            if (timeValue.isValid()) {
                const date1 = moment();
                const date2 = timeValue;
                if (
                    date1.isSame(date2, 'day') &&
                    date1.isSame(date2, 'month') &&
                    date1.isSame(date2, 'year') &&
                    +el.replace(':', '') <= +addTimeToDate(30).format('HHmm')
                ) {
                    return false;
                }
                return el;
            }
            return el.startsWith(timeString);
        });

        newIndex = res.findIndex((el) => +el.replace(':', '') + 4 >= +timeString.replace(':', ''));

        if (newIndex >= res.length || empty) {
            newIndex = 0;
        }

        setProposals(res);
        setIndex(newIndex);
    };

    useEffect(() => {
        if (modalRef?.current) {
            setTimeout(() => {
                const buttonElement = document.getElementById(`button-${index}`);
                if (buttonElement && open) {
                    buttonElement.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
            }, 100);
        }
    }, [open, timeValue, index]);

    const onClickTime = (newTimeString: string) => {
        const dateDuplicate = moment(validDate);
        dateDuplicate.set({
            h: +newTimeString.split(':')[0],
        });
        dateDuplicate.set({
            m: +newTimeString.split(':')[1],
        });
        onChangeTime(dateDuplicate, newTimeString);
    };

    if (!proposals?.length) {
        return null;
    }

    return (
        <div ref={modalRef} className={open ? classes.visibleDiv : classes.unvisibleDiv}>
            <Popper ref={popperRef} className={classes.zIndex} open={open} anchorEl={anchorEl} placement="bottom-end">
                <div className={classes.timePopper}>
                    {proposals.map((el, i) => (
                        <Button
                            id={`button-${i}`}
                            key={el}
                            type="button"
                            className={classes.buttonTimeStyle}
                            onMouseDown={() => {
                                onClickTime(el);
                            }}
                        >
                            {el}
                        </Button>
                    ))}
                </div>
            </Popper>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        timePopper: {
            display: 'flex',
            flexDirection: 'column',
            '& div::-webkit-scrollbar': {
                width: '0 !important',
            },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '6px',
            marginTop: 10,
            '& p': {
                marginLeft: 10,
            },
            maxHeight: 320,
            minHeight: 40,
            overflow: 'auto',
            width: 180,
            background: `${theme.palette.colors.background.secondaryBtn}!important`,
        },
        visibleDiv: {
            visibility: 'visible',
        },
        unvisibleDiv: {
            visibility: 'hidden',
        },
        buttonTimeStyle: {
            padding: theme.spacing(1),
            justifyContent: 'flex-start',
        },
        zIndex: {
            zIndex: 10,
        },
    }),
);

export default PopperTimePicker;
